// File: /src/components/finance/AdvancedReportFilters.js

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
	FaFilePdf,
	FaFileExcel,
	FaFilter,
	FaChartBar,
	FaTable,
	FaChartLine,
} from 'react-icons/fa';
import '../../styles/finance/AdvancedReportFilters.css';
import {
	generateCustomReport,
	generateFinanceSummaryReport,
	generateInvoiceReport,
	generateTemporalComparisonReport,
} from './FinanceGenReports';

const REPORT_TYPES = {
	SUMMARY: 'summary',
	INVOICES: 'invoices',
	TEMPORAL: 'temporal',
	CUSTOM: 'custom',
};

const INITIAL_FILTERS = {
	minValue: '',
	maxValue: '',
	startDate: null,
	endDate: null,
	type: 'all',
	descriptionKeyword: '',
};

function AdvancedReportFilters({
	entries,
	financeSummary = {},
	paidInvoices = [],
	lineData = [],
	period = 'monthly',
	projects = [],
	clients = [],
}) {
	
	const [filters, setFilters] = useState(INITIAL_FILTERS);
	const [selectedReportType, setSelectedReportType] = useState(
		REPORT_TYPES.SUMMARY
	);
	const [dateError, setDateError] = useState({ start: '', end: '' });
	const [dateInputValue, setDateInputValue] = useState({
		start: '',
		end: '',
	});

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFilters((prev) => ({ ...prev, [name]: value }));
	};

	const formatDateInput = (value) => {
		const digits = value.replace(/\D/g, '');
		let formatted = digits;
		if (digits.length > 2) {
			formatted = digits.slice(0, 2) + '/' + digits.slice(2);
		}
		if (digits.length > 4) {
			formatted =
				digits.slice(0, 2) +
				'/' +
				digits.slice(2, 4) +
				'/' +
				digits.slice(4, 8);
		}
		return formatted;
	};

	const validateDate = (dateStr) => {
		if (dateStr.length === 0) return true;
		const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
		if (!regex.test(dateStr)) return false;
		const [, day, month, year] = dateStr.match(regex);
		const date = new Date(year, month - 1, day);
		return (
			date &&
			date.getFullYear() === Number(year) &&
			date.getMonth() === Number(month) - 1 &&
			date.getDate() === Number(day)
		);
	};

	const handleDateChange = (e, field) => {
		const rawValue = e.target.value;
		const formattedValue = formatDateInput(rawValue);
		setDateInputValue((prev) => ({
			...prev,
			[field]: formattedValue,
		}));

		if (!validateDate(formattedValue)) {
			setDateError((prev) => ({
				...prev,
				[field]: 'Data inválida',
			}));
			setFilters((prev) => ({
				...prev,
				[field === 'start' ? 'startDate' : 'endDate']: null,
			}));
		} else {
			setDateError((prev) => ({
				...prev,
				[field]: '',
			}));
			if (formattedValue.length === 10) {
				const [day, month, year] = formattedValue.split('/');
				const date = new Date(year, month - 1, day);
				setFilters((prev) => ({
					...prev,
					[field === 'start' ? 'startDate' : 'endDate']: date,
				}));
			}
		}
	};

	const handleGenerateReport = (fileType) => {
		switch (selectedReportType) {
			case REPORT_TYPES.SUMMARY:
				generateFinanceSummaryReport(financeSummary, {}, fileType);
				break;
			case REPORT_TYPES.INVOICES:
				if (!projects.length || !clients.length) {
					toast.error('Dados de projetos ou clientes não carregados');
					return;
				}
				generateInvoiceReport(
					paidInvoices,
					projects,
					clients,
					{},
					fileType
				);
				break;
			case REPORT_TYPES.TEMPORAL:
				generateTemporalComparisonReport(
					lineData,
					period,
					{},
					fileType
				);
				break;
			case REPORT_TYPES.CUSTOM:
				const parsedFilters = {
					...filters,
					minValue: filters.minValue
						? parseFloat(filters.minValue)
						: undefined,
					maxValue: filters.maxValue
						? parseFloat(filters.maxValue)
						: undefined,
				};
				generateCustomReport(entries, parsedFilters, fileType);
				break;
			default:
				toast.error('Selecione um tipo de relatório');
		}
	};

	const renderReportTypeButtons = () => (
		<div className="report-actions-container">
			<div className="report-type-selector">
				<button
					className={`report-type-button ${
						selectedReportType === REPORT_TYPES.SUMMARY
							? 'active'
							: ''
					}`}
					onClick={() => setSelectedReportType(REPORT_TYPES.SUMMARY)}
				>
					<FaChartBar /> Resumo Financeiro
				</button>
				<button
					className={`report-type-button ${
						selectedReportType === REPORT_TYPES.INVOICES
							? 'active'
							: ''
					}`}
					onClick={() => setSelectedReportType(REPORT_TYPES.INVOICES)}
				>
					<FaTable /> Relatório de Faturas
				</button>
				<button
					className={`report-type-button ${
						selectedReportType === REPORT_TYPES.TEMPORAL
							? 'active'
							: ''
					}`}
					onClick={() => setSelectedReportType(REPORT_TYPES.TEMPORAL)}
				>
					<FaChartLine /> Comparação Temporal
				</button>
				<button
					className={`report-type-button ${
						selectedReportType === REPORT_TYPES.CUSTOM
							? 'active'
							: ''
					}`}
					onClick={() => setSelectedReportType(REPORT_TYPES.CUSTOM)}
				>
					<FaFilter /> Personalizado
				</button>
			</div>
		</div>
	);

	const renderExportButtons = () => (
		<div className="global-report-actions">
			<button
				className="format-button-pdf"
				onClick={() => handleGenerateReport('pdf')}
			>
				<FaFilePdf /> Exportar PDF
			</button>
			<button
				className="format-button-xlsx"
				onClick={() => handleGenerateReport('xlsx')}
			>
				<FaFileExcel /> Exportar XLSX
			</button>
		</div>
	);

	const renderCustomFilters = () => (
		<div className="filter-grid">
			<div className="filter-item">
				<label>Valor Mínimo</label>
				<input
					type="number"
					name="minValue"
					value={filters.minValue}
					onChange={handleChange}
					placeholder="R$ 0,00"
				/>
			</div>
			<div className="filter-item">
				<label>Valor Máximo</label>
				<input
					type="number"
					name="maxValue"
					value={filters.maxValue}
					onChange={handleChange}
					placeholder="R$ 999.999,99"
				/>
			</div>
			<div className="filter-item">
				<label>Tipo de Transação</label>
				<select
					name="type"
					value={filters.type}
					onChange={handleChange}
				>
					<option value="all">Todas as Transações</option>
					<option value="income">Apenas Receitas</option>
					<option value="expense">Apenas Despesas</option>
					<option value="invoice">Apenas Faturas</option>
				</select>
			</div>
			<div className="filter-item">
				<label>Data Inicial</label>
				<div className="date-input-container">
					<input
						type="text"
						placeholder="DD/MM/AAAA"
						value={dateInputValue.start}
						onChange={(e) => handleDateChange(e, 'start')}
						autoComplete="off"
					/>
					{dateError.start && (
						<span className="date-error">{dateError.start}</span>
					)}
				</div>
			</div>
			<div className="filter-item">
				<label>Data Final</label>
				<div className="date-input-container">
					<input
						type="text"
						placeholder="DD/MM/AAAA"
						value={dateInputValue.end}
						onChange={(e) => handleDateChange(e, 'end')}
						autoComplete="off"
					/>
					{dateError.end && (
						<span className="date-error">{dateError.end}</span>
					)}
				</div>
			</div>
			<div className="filter-item">
				<label>Buscar na Descrição</label>
				<input
					type="text"
					name="descriptionKeyword"
					value={filters.descriptionKeyword}
					onChange={handleChange}
					placeholder="Digite palavras-chave"
				/>
			</div>
		</div>
	);

	return (
		<div className="advanced-report-filters">
			{renderReportTypeButtons()}
			{selectedReportType === REPORT_TYPES.CUSTOM && (
				<>
					{renderCustomFilters()}
					{renderExportButtons()}
				</>
			)}
			{selectedReportType !== REPORT_TYPES.CUSTOM &&
				renderExportButtons()}
		</div>
	);
}
export default AdvancedReportFilters;
